import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Search,
  Filter,
  Download,
  UserPlus,
  RefreshCw,
  UserMinus,
  X,
  History,
  Phone,
  ChevronLeft,
  ChevronRight,
  Bell,
} from "lucide-react";
import { apiUrl } from "./LoginSignup";
import Loader from "./Loader";
import "./LeadManagement.css";

const SRDEV_ROLE = "srdev";
const BDM_ROLES = ["bdm"];

const DEFAULT_STATUS_OPTIONS = [
  "new",
  "answered",
  "switch off",
  "no incoming",
  "cut the call",
  "call back",
  "language issue",
  "interested",
  "qualified",
  "not interested",
  "do not pick",
  "busy",
  "network issue",
  "dnd",
];

const INDIAN_STATES = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const LeadManagement = () => {
  const userSession = useMemo(
    () => JSON.parse(localStorage.getItem("userSession")) || {},
    []
  );
  const isSrdev = userSession.user_role === SRDEV_ROLE;
  const isLeadManager = isSrdev;
  const isBdm = BDM_ROLES.includes(userSession.user_role);
  const canAccess = isSrdev || isBdm;

  const authHeaders = useMemo(
    () => ({ authorization: userSession.token }),
    [userSession.token]
  );

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [bdmList, setBdmList] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    sources: [],
    schemes: [],
    statuses: [],
    states: [],
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    source: "",
    scheme: "",
    state: "",
    dateFrom: "",
    dateTo: "",
    assignedBdmId: "",
    unassigned: "",
  });

  const [sort, setSort] = useState({ sortBy: "lead_date", sortOrder: "desc" });

  // Modals
  const [assignModal, setAssignModal] = useState({ open: false, mode: "assign" });
  const [assignRoundRobin, setAssignRoundRobin] = useState(false);
  const [selectedBdms, setSelectedBdms] = useState([]);
  const [historyModal, setHistoryModal] = useState({ open: false, leadId: null, data: [] });
  const [activityModal, setActivityModal] = useState({ open: false, leadId: null, leadName: "", data: [] });
  const [detailModal, setDetailModal] = useState({ open: false, lead: null, assignment: null });
  const [callForm, setCallForm] = useState({
    status: "",
    remarks: "",
    follow_up_date: "",
  });
  const [callHistory, setCallHistory] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    reminder_date: "",
    reminder_time: "",
    reminder_note: "",
  });
  const [pendingReminder, setPendingReminder] = useState(null);
  const [dueReminders, setDueReminders] = useState([]);
  const [dismissingReminderId, setDismissingReminderId] = useState(null);

  const statusOptions = useMemo(() => {
    const removed = new Set([
      "converted",
      "contacted",
      "lost",
      "follow up",
    ]);
    const fromDb = (filterOptions.statuses || []).filter(
      (s) => !removed.has(String(s).trim().toLowerCase())
    );
    return [...new Set([...DEFAULT_STATUS_OPTIONS, ...fromDb])];
  }, [filterOptions.statuses]);

  const stateOptions = useMemo(() => {
    const fromDb = (filterOptions.states || []).filter((s) =>
      INDIAN_STATES.some(
        (state) => state.toLowerCase() === String(s).trim().toLowerCase()
      )
    );
    return [...new Set([...INDIAN_STATES, ...fromDb])];
  }, [filterOptions.states]);

  const fetchDueReminders = useCallback(async () => {
    if (!isBdm || !userSession?.token) return;
    try {
      const res = await axios.get(`${apiUrl}/leads/reminders/active`, {
        headers: authHeaders,
      });
      setDueReminders(res.data || []);
    } catch {
      setDueReminders([]);
    }
  }, [isBdm, userSession?.token, authHeaders]);

  const handleDismissReminder = async (reminderId) => {
    setDismissingReminderId(reminderId);
    try {
      await axios.patch(
        `${apiUrl}/leads/reminders/${reminderId}/dismiss`,
        {},
        { headers: authHeaders }
      );
      setDueReminders((prev) => prev.filter((r) => r.id !== reminderId));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to dismiss reminder");
    } finally {
      setDismissingReminderId(null);
    }
  };

  const buildQueryParams = useCallback(
    (page = pagination.page) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pagination.limit),
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });

      return params.toString();
    },
    [filters, pagination.limit, pagination.page, sort]
  );

  const fetchLeads = useCallback(
    async (page = 1) => {
      if (!canAccess) return;

      try {
        setLoading(true);
        setError("");
        const res = await axios.get(`${apiUrl}/leads?${buildQueryParams(page)}`, {
          headers: authHeaders,
        });
        setLeads(res.data.data || []);
        setPagination(res.data.pagination || pagination);
        setSelectedIds([]);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch leads");
      } finally {
        setLoading(false);
      }
    },
    [canAccess, buildQueryParams, authHeaders, pagination]
  );

  const fetchFilterOptions = async () => {
    if (!canAccess) return;
    try {
      const res = await axios.get(`${apiUrl}/leads/filter-options`, {
        headers: authHeaders,
      });
      setFilterOptions(res.data);
    } catch (err) {
      console.error("Failed to load filter options", err);
    }
  };

  const fetchBdmList = async () => {
    if (!isLeadManager) return;
    try {
      const res = await axios.get(`${apiUrl}/user/bdm-list`, {
        headers: authHeaders,
      });
      setBdmList(res.data || []);
    } catch (err) {
      console.error("Failed to load BDM list", err);
    }
  };

  useEffect(() => {
    if (!canAccess) {
      setLoading(false);
      setError("You do not have permission to access Lead Management.");
      return;
    }
    fetchLeads(1);
    fetchFilterOptions();
    fetchBdmList();
    if (isBdm) fetchDueReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  useEffect(() => {
    if (!isBdm) return;
    const interval = setInterval(fetchDueReminders, 15000);
    return () => clearInterval(interval);
  }, [isBdm, fetchDueReminders]);

  useEffect(() => {
    if (canAccess) fetchLeads(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => fetchLeads(1);

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      source: "",
      scheme: "",
      state: "",
      dateFrom: "",
      dateTo: "",
      assignedBdmId: "",
      unassigned: "",
    });
    setTimeout(() => fetchLeads(1), 0);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === leads.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(leads.map((l) => l.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSort = (column) => {
    setSort((prev) => ({
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === "desc" ? "asc" : "desc",
    }));
  };

  const openAssignModal = (mode) => {
    if (!selectedIds.length) {
      setError("Please select at least one lead");
      return;
    }
    setSelectedBdms([]);
    setAssignRoundRobin(false);
    setAssignModal({ open: true, mode });
    setError("");
  };

  const handleAssign = async () => {
    if (!selectedBdms.length) {
      setError("Please select at least one BDM");
      return;
    }

    setActionLoading(true);
    setError("");

    try {
      const endpoint =
        assignModal.mode === "reassign" ? "/leads/reassign" : "/leads/assign";

      await axios.post(
        `${apiUrl}${endpoint}`,
        {
          leadIds: selectedIds,
          bdmIds: selectedBdms,
          strategy: assignRoundRobin ? "round-robin" : "all",
        },
        { headers: authHeaders }
      );

      setSuccess(
        assignModal.mode === "reassign"
          ? assignRoundRobin
            ? "Leads reassigned via round-robin"
            : "Leads reassigned successfully"
          : assignRoundRobin
            ? "Leads assigned via round-robin"
            : "Leads assigned successfully"
      );
      setAssignModal({ open: false, mode: "assign" });
      fetchLeads(pagination.page);
    } catch (err) {
      setError(err.response?.data?.message || "Assignment failed");
    } finally {
      setActionLoading(false);
    }
  };

  const selectedUnassignableIds = useMemo(
    () =>
      leads
        .filter((l) => selectedIds.includes(l.id) && l.can_unassign)
        .map((l) => l.id),
    [leads, selectedIds]
  );

  const handleUnassign = async (leadIdsToUnassign = selectedUnassignableIds) => {
    if (!leadIdsToUnassign.length) {
      setError(
        "Select assigned leads with status 'new' that have no BDM call activity to remove assignment"
      );
      return;
    }

    const confirmed = window.confirm(
      `Remove assignment for ${leadIdsToUnassign.length} lead(s)?\n\n` +
      "Only works for 'new' leads where the BDM has not made any call. " +
      "Leads will return to the unassigned pool for reassignment."
    );
    if (!confirmed) return;

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await axios.post(
        `${apiUrl}/leads/unassign`,
        { leadIds: leadIdsToUnassign },
        { headers: authHeaders }
      );

      if (res.data.removed > 0) {
        setSuccess(res.data.message);
        setSelectedIds([]);
        fetchLeads(pagination.page);
      } else {
        const errMsg =
          res.data.errors?.map((e) => `Lead #${e.leadId}: ${e.message}`).join("\n") ||
          res.data.message;
        setError(errMsg);
      }
    } catch (err) {
      const data = err.response?.data;
      const errMsg =
        data?.errors?.map((e) => `Lead #${e.leadId}: ${e.message}`).join(" | ") ||
        data?.message ||
        "Failed to remove assignment";
      setError(errMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async (exportSelected = false) => {
    setActionLoading(true);
    setError("");

    try {
      const query = buildQueryParams();
      const res = await axios.post(
        `${apiUrl}/leads/export?${query}`,
        { leadIds: exportSelected ? selectedIds : undefined },
        {
          headers: authHeaders,
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `leads_export_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess("Export downloaded successfully");
    } catch (err) {
      setError("Export failed");
    } finally {
      setActionLoading(false);
    }
  };

  const openHistoryModal = async (leadId) => {
    try {
      const res = await axios.get(`${apiUrl}/leads/${leadId}/assignment-history`, {
        headers: authHeaders,
      });
      setHistoryModal({ open: true, leadId, data: res.data });
    } catch (err) {
      setError("Failed to load assignment history");
    }
  };

  const openActivityModal = async (lead) => {
    try {
      const res = await axios.get(`${apiUrl}/leads/${lead.id}/bdm-activity`, {
        headers: authHeaders,
      });
      setActivityModal({
        open: true,
        leadId: lead.id,
        leadName: lead.name,
        data: res.data,
      });
    } catch (err) {
      setError("Failed to load BDM activity");
    }
  };

  const openDetailModal = async (lead) => {
    const myAssignment = lead.assignments?.find(
      (a) => a.bdm_id === userSession.user_id
    );

    setDetailModal({ open: true, lead, assignment: myAssignment });
    setCallForm({
      status: lead.status || "",
      remarks: myAssignment?.remarks || "",
      follow_up_date: myAssignment?.follow_up_date || "",
    });
    setReminderForm({ reminder_date: "", reminder_time: "", reminder_note: "" });
    setPendingReminder(null);

    if (myAssignment?.id) {
      try {
        const [historyRes, reminderRes] = await Promise.all([
          axios.get(
            `${apiUrl}/leads/assignments/${myAssignment.id}/call-history`,
            { headers: authHeaders }
          ),
          axios.get(
            `${apiUrl}/leads/assignments/${myAssignment.id}/reminder`,
            { headers: authHeaders }
          ),
        ]);
        setCallHistory(historyRes.data || []);
        const rem = reminderRes.data;
        if (rem) {
          setPendingReminder(rem);
          const dt = new Date(rem.reminder_at);
          if (!Number.isNaN(dt.getTime())) {
            setReminderForm({
              reminder_date: rem.reminder_at.slice(0, 10),
              reminder_time: rem.reminder_at.slice(11, 16),
              reminder_note: rem.reminder_note || "",
            });
          }
        }
      } catch {
        setCallHistory([]);
      }
    } else {
      setCallHistory([]);
    }
  };

  const handleSetReminder = async () => {
    const { assignment } = detailModal;
    if (!assignment?.id) return;

    if (!reminderForm.reminder_date || !reminderForm.reminder_time) {
      setError("Please set both reminder date and time");
      return;
    }

    const reminder_at = `${reminderForm.reminder_date} ${reminderForm.reminder_time}:00`;

    setActionLoading(true);
    setError("");

    try {
      const res = await axios.post(
        `${apiUrl}/leads/assignments/${assignment.id}/reminder`,
        {
          reminder_at,
          reminder_note: reminderForm.reminder_note,
        },
        { headers: authHeaders }
      );
      setPendingReminder(res.data.reminder);
      setSuccess("Reminder saved");
      fetchDueReminders();
      setSuccess("Reminder set — you will be notified on your dashboard when it's due");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to set reminder");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveCall = async () => {
    const { assignment } = detailModal;
    if (!assignment?.id) return;

    setActionLoading(true);
    setError("");

    try {
      await axios.post(
        `${apiUrl}/leads/assignments/${assignment.id}/call-history`,
        callForm,
        { headers: authHeaders }
      );
      setSuccess("Call logged successfully");
      setDetailModal({ open: false, lead: null, assignment: null });
      fetchLeads(pagination.page);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save call");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateFields = async () => {
    const { assignment } = detailModal;
    if (!assignment?.id) return;

    setActionLoading(true);

    try {
      await axios.patch(
        `${apiUrl}/leads/assignments/${assignment.id}`,
        callForm,
        { headers: authHeaders }
      );
      setSuccess("Lead updated successfully");
      fetchLeads(pagination.page);
    } catch (err) {
      setError(err.response?.data?.message || "Update failed");
    } finally {
      setActionLoading(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="lead-management">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="lead-management">
      <div className="page-header">
        <h1>Lead Management</h1>
        <span className="selected-count">
          {selectedIds.length > 0 && `${selectedIds.length} selected`}
        </span>
      </div>

      {isBdm && dueReminders.length > 0 && (
        <div className="lead-due-reminders" role="alert" aria-live="polite">
          <div className="lead-due-reminders-header">
            <Bell size={18} />
            <strong>Due Reminders ({dueReminders.length})</strong>
            <span>These stay visible until you mark them complete</span>
          </div>
          <div className="lead-due-reminders-list">
            {dueReminders.map((reminder) => (
              <div key={reminder.id} className="lead-due-reminder-card">
                <div className="lead-due-reminder-body">
                  <div className="lead-due-reminder-title">
                    {reminder.lead_name || "Lead"}
                    {reminder.lead_number && (
                      <span className="lead-due-reminder-phone"> · {reminder.lead_number}</span>
                    )}
                  </div>
                  {reminder.lead_company && (
                    <div className="lead-due-reminder-meta">{reminder.lead_company}</div>
                  )}
                  {reminder.reminder_note && (
                    <div className="lead-due-reminder-note">{reminder.reminder_note}</div>
                  )}
                  <div className="lead-due-reminder-time">
                    Due: {new Date(reminder.reminder_at).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => handleDismissReminder(reminder.id)}
                  disabled={dismissingReminderId === reminder.id}
                >
                  {dismissingReminderId === reminder.id ? "Closing..." : "Mark Complete & Dismiss"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
          <button
            type="button"
            className="action-link"
            style={{ marginLeft: 12 }}
            onClick={() => setError("")}
          >
            Dismiss
          </button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          {success}
          <button
            type="button"
            className="action-link"
            style={{ marginLeft: 12 }}
            onClick={() => setSuccess("")}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="toolbar">
        <div className="search-box">
          <Search size={16} color="#9ca3af" />
          <input
            type="text"
            placeholder="Search name, phone, email..."
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
          />
        </div>

        <div className="toolbar-actions toolbar-actions-compact">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={14} /> Filters
          </button>

          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fetchLeads(pagination.page)}>
            <RefreshCw size={14} /> Refresh
          </button>

          {isLeadManager && (
            <>
              <Link to="/dashboard/lead-campaigns" className="btn btn-secondary btn-sm">
                Campaigns
              </Link>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={() => handleExport(false)}
                disabled={actionLoading}
              >
                <Download size={14} /> Export
              </button>

              {selectedIds.length > 0 && (
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => handleExport(true)}
                  disabled={actionLoading}
                >
                  <Download size={14} /> Export ({selectedIds.length})
                </button>
              )}

              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => openAssignModal("assign")}
                disabled={!selectedIds.length}
              >
                <UserPlus size={14} /> Assign
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => openAssignModal("reassign")}
                disabled={!selectedIds.length}
              >
                <RefreshCw size={14} /> Reassign
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => handleUnassign()}
                disabled={!selectedUnassignableIds.length || actionLoading}
                title="Remove assignment for new leads with no BDM calls"
              >
                <UserMinus size={14} /> Unassign
                {selectedUnassignableIds.length > 0 && ` (${selectedUnassignableIds.length})`}
              </button>
            </>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="filters-panel filters-panel-compact">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">All</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Source</label>
            <select
              value={filters.source}
              onChange={(e) => handleFilterChange("source", e.target.value)}
            >
              <option value="">All</option>
              {filterOptions.sources.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Scheme / Campaign</label>
            <select
              value={filters.scheme}
              onChange={(e) => handleFilterChange("scheme", e.target.value)}
            >
              <option value="">All</option>
              {filterOptions.schemes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>State</label>
            <select
              value={filters.state}
              onChange={(e) => handleFilterChange("state", e.target.value)}
            >
              <option value="">All</option>
              {stateOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          {isLeadManager && (
            <>
              <div className="filter-group">
                <label>Assigned BDM</label>
                <select
                  value={filters.assignedBdmId}
                  onChange={(e) => handleFilterChange("assignedBdmId", e.target.value)}
                >
                  <option value="">All</option>
                  {bdmList.map((b) => (
                    <option key={b._id} value={b._id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Unassigned Only</label>
                <select
                  value={filters.unassigned}
                  onChange={(e) => handleFilterChange("unassigned", e.target.value)}
                >
                  <option value="">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
            </>
          )}
          <div className="filter-group">
            <label>Date From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Date To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
            />
          </div>
          <div className="filter-group filter-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={applyFilters}>
              Apply
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        {loading ? (
          <div className="loading"><Loader /></div>
        ) : (
          <>
            <table className="responsive-table">
              <thead>
                <tr>
                  {isLeadManager && (
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={leads.length > 0 && selectedIds.length === leads.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                  )}
                  <th onClick={() => handleSort("id")}>ID</th>
                  <th onClick={() => handleSort("name")}>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th onClick={() => handleSort("company")}>Company</th>
                  <th onClick={() => handleSort("source")}>Source</th>
                  <th onClick={() => handleSort("scheme")}>Scheme</th>
                  <th onClick={() => handleSort("status")}>Status</th>
                  <th onClick={() => handleSort("lead_date")}>Lead Date</th>
                  {isLeadManager && <th>Assigned BDMs</th>}
                  <th className="actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr className="empty-row">
                    <td colSpan={isLeadManager ? 12 : 10} className="empty-cell" data-label="">
                      No leads found
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => {
                    const myAssignment = lead.assignments?.find(
                      (a) => a.bdm_id === userSession.user_id
                    );

                    return (
                      <tr key={lead.id}>
                        {isLeadManager && (
                          <td className="checkbox-cell" data-label="Select">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(lead.id)}
                              onChange={() => toggleSelect(lead.id)}
                            />
                          </td>
                        )}
                        <td data-label="ID">{lead.id}</td>
                        <td data-label="Name">{lead.name}</td>
                        <td data-label="Phone">{lead.number}</td>
                        <td data-label="Email">{lead.email}</td>
                        <td data-label="Company">{lead.company}</td>
                        <td data-label="Source">{lead.source}</td>
                        <td data-label="Scheme">{lead.scheme}</td>
                        <td data-label="Status">
                          <span className={`status-badge ${lead.status === "new" ? "status-new" : "status-default"}`}>
                            {lead.status}
                          </span>
                        </td>
                        <td data-label="Lead Date">{lead.lead_date}</td>
                        {isLeadManager && <td data-label="Assigned BDMs">{lead.assigned_bdms || "—"}</td>}
                        <td className="actions-cell" data-label="Actions">
                          <div className="action-buttons action-buttons-compact">
                            {isBdm && myAssignment && (
                              <button
                                type="button"
                                className="action-link action-link-sm"
                                onClick={() => openDetailModal(lead)}
                                title="Update lead"
                              >
                                <Phone size={12} /> Update
                              </button>
                            )}
                            {isLeadManager && (
                              <>
                                <button
                                  type="button"
                                  className="action-link action-link-sm"
                                  onClick={() => openActivityModal(lead)}
                                  title="BDM Activity"
                                >
                                  <Phone size={12} /> Activity
                                </button>
                                <button
                                  type="button"
                                  className="action-link action-link-sm"
                                  onClick={() => openHistoryModal(lead.id)}
                                  title="Assignment history"
                                >
                                  <History size={12} /> Assign
                                </button>
                                {lead.can_unassign && (
                                  <button
                                    type="button"
                                    className="action-link action-link-sm danger-btn"
                                    onClick={() => handleUnassign([lead.id])}
                                    title="Unassign lead"
                                  >
                                    <UserMinus size={12} /> Unassign
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            <div className="pagination">
              <div className="pagination-info">
                Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} leads)
              </div>
              <div className="pagination-controls">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchLeads(pagination.page - 1)}
                >
                  <ChevronLeft size={16} /> Prev
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchLeads(pagination.page + 1)}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Assign / Reassign Modal */}
      {assignModal.open && (
        <div className="modal-overlay" onClick={() => setAssignModal({ open: false, mode: "assign" })}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{assignModal.mode === "reassign" ? "Reassign Leads" : "Assign Leads"}</h2>
              <button type="button" className="action-link modal-close" onClick={() => setAssignModal({ open: false, mode: "assign" })}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 12, fontSize: 14, color: "#6b7280" }}>
                {selectedIds.length} lead(s) selected. Choose one or more BDMs:
              </p>
              <label className="campaign-toggle" style={{ marginBottom: 12, display: "flex", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={assignRoundRobin}
                  onChange={(e) => setAssignRoundRobin(e.target.checked)}
                />
                Use round-robin (one BDM per lead in rotation)
              </label>
              <div className="bdm-checkboxes">
                {bdmList.map((bdm) => (
                  <label key={bdm._id}>
                    <input
                      type="checkbox"
                      checked={selectedBdms.includes(bdm._id)}
                      onChange={() =>
                        setSelectedBdms((prev) =>
                          prev.includes(bdm._id)
                            ? prev.filter((id) => id !== bdm._id)
                            : [...prev, bdm._id]
                        )
                      }
                    />
                    {bdm.name} ({bdm.user_role})
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setAssignModal({ open: false, mode: "assign" })}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleAssign} disabled={actionLoading}>
                {actionLoading ? "Processing..." : assignModal.mode === "reassign" ? "Reassign" : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment History Modal */}
      {historyModal.open && (
        <div className="modal-overlay" onClick={() => setHistoryModal({ open: false, leadId: null, data: [] })}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assignment History — Lead #{historyModal.leadId}</h2>
              <button type="button" className="action-link modal-close" onClick={() => setHistoryModal({ open: false, leadId: null, data: [] })}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {historyModal.data.length === 0 ? (
                <p>No assignment history</p>
              ) : (
                <ul className="history-list">
                  {historyModal.data.map((h) => (
                    <li key={h.id}>
                      <strong>{h.bdm_name}</strong>
                      {h.is_active ? " (Active)" : " (Inactive)"}
                      <br />
                      Assigned by {h.assigned_by_name} on {new Date(h.assigned_at).toLocaleString()}
                      {!h.is_active && h.deactivated_at && (
                        <>
                          <br />
                          Deactivated: {new Date(h.deactivated_at).toLocaleString()} — {h.deactivated_reason}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BDM Detail / Call Update Modal */}
      {detailModal.open && detailModal.lead && (
        <div className="modal-overlay" onClick={() => setDetailModal({ open: false, lead: null, assignment: null })}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{detailModal.lead.name} — Update Lead</h2>
              <button type="button" className="action-link modal-close" onClick={() => setDetailModal({ open: false, lead: null, assignment: null })}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="label">Phone</div>
                  <div className="value">{detailModal.lead.number}</div>
                </div>
                <div className="detail-item">
                  <div className="label">Email</div>
                  <div className="value">{detailModal.lead.email}</div>
                </div>
                <div className="detail-item">
                  <div className="label">Company</div>
                  <div className="value">{detailModal.lead.company}</div>
                </div>
                <div className="detail-item">
                  <div className="label">Scheme</div>
                  <div className="value">{detailModal.lead.scheme}</div>
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={callForm.status}
                  onChange={(e) => setCallForm({ ...callForm, status: e.target.value })}
                >
                  <option value="">Select status</option>
                  {statusOptions.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Follow-up Date</label>
                <input
                  type="date"
                  value={callForm.follow_up_date || ""}
                  onChange={(e) => setCallForm({ ...callForm, follow_up_date: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Remarks</label>
                <textarea
                  value={callForm.remarks}
                  onChange={(e) => setCallForm({ ...callForm, remarks: e.target.value })}
                  placeholder="Call notes..."
                />
              </div>

              {isBdm && detailModal.assignment && (
                <div className="reminder-section">
                  <h3 className="reminder-section-title">
                    <Bell size={16} /> Lead Reminder
                  </h3>
                  <p className="reminder-section-hint">
                    Set a reminder for this lead. When due, a notification will appear on your
                    dashboard until you dismiss it.
                  </p>
                  {pendingReminder && (
                    <div className="reminder-pending-badge">
                      Active reminder: {new Date(pendingReminder.reminder_at).toLocaleString()}
                    </div>
                  )}
                  <div className="reminder-datetime-row">
                    <div className="form-group">
                      <label>Reminder Date</label>
                      <input
                        type="date"
                        value={reminderForm.reminder_date}
                        onChange={(e) =>
                          setReminderForm({ ...reminderForm, reminder_date: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Reminder Time</label>
                      <input
                        type="time"
                        value={reminderForm.reminder_time}
                        onChange={(e) =>
                          setReminderForm({ ...reminderForm, reminder_time: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Reminder Note (optional)</label>
                    <input
                      type="text"
                      value={reminderForm.reminder_note}
                      onChange={(e) =>
                        setReminderForm({ ...reminderForm, reminder_note: e.target.value })
                      }
                      placeholder="e.g. Call back about loan documents"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSetReminder}
                    disabled={actionLoading}
                  >
                    <Bell size={14} /> {pendingReminder ? "Update Reminder" : "Set Reminder"}
                  </button>
                </div>
              )}

              {callHistory.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Activity History</h3>
                  <ul className="history-list">
                    {callHistory.map((c) => (
                      <li key={c.id}>
                        <strong>{c.status || c.call_status || "—"}</strong>
                        <br />
                        {c.remarks && <span>{c.remarks}<br /></span>}
                        <small>{new Date(c.called_at).toLocaleString()}</small>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setDetailModal({ open: false, lead: null, assignment: null })}>
                Close
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleUpdateFields} disabled={actionLoading}>
                Save Fields
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveCall} disabled={actionLoading}>
                {actionLoading ? "Saving..." : "Save & Log Activity"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BDM Activity Modal (SRDEV) */}
      {activityModal.open && (
        <div className="modal-overlay" onClick={() => setActivityModal({ open: false, leadId: null, leadName: "", data: [] })}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>BDM Activity — {activityModal.leadName} (#{activityModal.leadId})</h2>
              <button type="button" className="action-link modal-close" onClick={() => setActivityModal({ open: false, leadId: null, leadName: "", data: [] })}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {activityModal.data.length === 0 ? (
                <p>No BDM activity recorded yet for this lead.</p>
              ) : (
                <ul className="history-list">
                  {activityModal.data.map((a) => (
                    <li key={a.id}>
                      <strong>{a.called_by_name || a.bdm_name}</strong>
                      {" — "}
                      <span className={`status-badge ${a.status === "new" ? "status-new" : "status-default"}`}>
                        {a.status || "—"}
                      </span>
                      <br />
                      {a.remarks && <span>{a.remarks}<br /></span>}
                      {a.follow_up_date && (
                        <small>Follow-up: {a.follow_up_date}<br /></small>
                      )}
                      <small>{new Date(a.called_at).toLocaleString()}</small>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManagement;
